import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box, Typography, Avatar, Chip, Divider, Card, CardContent,
    Skeleton, Alert, Tooltip, IconButton,
} from "@mui/material";
import ArrowBackIcon           from "@mui/icons-material/ArrowBackRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PeopleRoundedIcon       from "@mui/icons-material/PeopleRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import EmailOutlinedIcon       from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon       from "@mui/icons-material/PhoneOutlined";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import VerifiedRoundedIcon     from "@mui/icons-material/VerifiedRounded";
import SpeedRoundedIcon        from "@mui/icons-material/SpeedRounded";
import {
    PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import { decryptId, encryptId } from "../helper/idCodec.js"; // eslint-disable-line no-unused-vars

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const avatarBg = (name = "") => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n = "") => { const p = n.trim().split(" "); return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase(); };
const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : "—";

// ── Tab button ────────────────────────────────────────────────────────────
const TabBtn = ({ label, icon, active, onClick }) => (
    <Box onClick={onClick} sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 2.5, py: 1.2, borderRadius: "10px", cursor: "pointer",
        bgcolor: active ? T.teal : "transparent",
        color:   active ? "#fff" : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 13,
        transition: "all 0.15s",
        "&:hover": { bgcolor: active ? T.teal : T.tealLt, color: active ? "#fff" : T.teal },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        flexShrink: 0,
    }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        {label}
    </Box>
);

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ title, value, icon, iconBg, iconColor, accent, sub, loading }) => (
    <Card sx={{
        flex: "1 1 140px", borderRadius: "16px", border: `1px solid ${T.border}`,
        boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden",
    }}>
        <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.8 }}>
                        {title}
                    </Typography>
                    {loading ? <Skeleton width={50} height={28} /> :
                        <Typography sx={{ fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {value}
                        </Typography>
                    }
                    {sub && <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.4 }}>{sub}</Typography>}
                </Box>
                <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Info row ──────────────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", gap: 2, py: 1.5, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 16, color: T.teal } })}
        </Box>
        <Box>
            <Typography sx={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.text, mt: 0.2 }}>{value || "—"}</Typography>
        </Box>
    </Box>
);

// ── Truck card — clickable, navigates to truck details page ──────────────
const TruckCard = ({ truck, onView }) => {
    const onTrip = Boolean(truck.tagged_to_trip);
    return (
        <Card
            onClick={() => onView && onView(truck.truck_id)}
            sx={{
                borderRadius: "14px", border: `1px solid ${T.border}`,
                boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                overflow: "hidden", cursor: "pointer",
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 6px 18px rgba(0,124,132,0.14)`, borderColor: T.teal },
            }}
        >
            <Box sx={{ height: 3, bgcolor: onTrip ? T.teal : "#E2E8F0" }} />
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {truck.truck_pic
                            ? <img src={truck.truck_pic} alt={truck.truck_name} style={{ width: 40, height: 36, objectFit: "cover", borderRadius: 8 }} />
                            : <LocalShippingRoundedIcon sx={{ fontSize: 22, color: T.teal }} />
                        }
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>{truck.truck_name}</Typography>
                            <Chip label={onTrip ? "In Trip" : "Available"} size="small" sx={{
                                fontSize: 10, fontWeight: 700, height: 20, borderRadius: "6px",
                                bgcolor: onTrip ? T.tealLt : "#F1F5F9",
                                color:   onTrip ? T.teal  : T.muted,
                                border: `1px solid ${onTrip ? T.teal+"44" : T.border}`,
                            }} />
                        </Box>
                        <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.3 }}>{truck.truck_no}</Typography>
                        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                            <Typography sx={{ fontSize: 11, color: T.muted }}>
                                <strong style={{ color: T.text }}>{truck.capacity_inkgs}</strong> kg
                            </Typography>
                            {truck.capacity_inspace && (
                                <Typography sx={{ fontSize: 11, color: T.muted }}>
                                    <strong style={{ color: T.text }}>{truck.capacity_inspace}</strong> m³
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Agent card ────────────────────────────────────────────────────────────
const AgentCard = ({ agent }) => {
    const name  = `${agent.first_name || ""} ${agent.last_name || ""}`.trim();
    const [bg, fg] = avatarBg(name);
    const isActive = (agent.status || "").toLowerCase() === "active";
    return (
        <Card sx={{
            borderRadius: "14px", border: `1px solid ${T.border}`,
            boxShadow: "0 1px 3px rgba(15,23,42,0.05)", overflow: "hidden",
        }}>
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${T.teal}, ${T.tealLt})` }} />
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <Avatar src={agent.pic} sx={{ width: 44, height: 44, bgcolor: bg, color: fg, fontSize: 15, fontWeight: 800 }}>
                        {!agent.pic && initials(name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text }}>{name || "—"}</Typography>
                                {agent.agent_type && (
                                    <Typography sx={{ fontSize: 11, color: T.teal, fontWeight: 600, textTransform: "capitalize" }}>
                                        {agent.agent_type}
                                    </Typography>
                                )}
                            </Box>
                            <Chip label={isActive ? "Active" : "Inactive"} size="small" sx={{
                                fontSize: 10, fontWeight: 700, height: 20, borderRadius: "6px",
                                bgcolor: isActive ? T.emeraldLt : T.roseLt,
                                color:   isActive ? T.emerald   : T.rose,
                            }} />
                        </Box>
                        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.4 }}>
                            {agent.mobile_num && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                    <PhoneOutlinedIcon sx={{ fontSize: 13, color: T.muted }} />
                                    <Typography sx={{ fontSize: 12, color: T.muted }}>{agent.mobile_num}</Typography>
                                </Box>
                            )}
                            {agent.email && (
                                <Tooltip title={agent.email}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                        <EmailOutlinedIcon sx={{ fontSize: 13, color: T.muted }} />
                                        <Typography sx={{ fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                                            {agent.email}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            )}
                            {agent.createdAt && (
                                <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.2 }}>
                                    Joined {fmtDate(agent.createdAt)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Section heading ───────────────────────────────────────────────────────
const SectionHead = ({ title, count }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
        {count != null && (
            <Box sx={{ px: "8px", py: "2px", bgcolor: T.tealLt, borderRadius: "7px", fontSize: 12, fontWeight: 700, color: T.teal }}>
                {count}
            </Box>
        )}
    </Box>
);

// ── Tabs ──────────────────────────────────────────────────────────────────
const TABS = [
    { key: "dashboard", label: "Dashboard",  icon: <SpeedRoundedIcon /> },
    { key: "details",   label: "Details",    icon: <VerifiedRoundedIcon /> },
    { key: "team",      label: "My Team",    icon: <SupportAgentRoundedIcon /> },
];

// ── Main component ────────────────────────────────────────────────────────
export default function TruckOwnerDetails() {
    const navigate  = useNavigate();
    const location  = useLocation();
    const params    = new URLSearchParams(location.search);
    const ownerId   = params.get("id") ? decryptId(decodeURIComponent(params.get("id"))) : "";

    const [owner,         setOwner]         = useState(null);
    const [counts,        setCounts]        = useState(null);
    const [agents,        setAgents]        = useState([]);
    const [ownerLoading,  setOwnerLoading]  = useState(true);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [finSummary,    setFinSummary]    = useState(null);   // owner-summary
    const [finOwed,       setFinOwed]       = useState(null);   // trips-owed
    const [finLoading,    setFinLoading]    = useState(false);
    const [error,         setError]         = useState(null);
    const [tab,           setTab]           = useState("dashboard");

    const fetchOwner = useCallback(async () => {
        if (!ownerId) { setOwnerLoading(false); setError("Invalid owner ID"); return; }
        setOwnerLoading(true);
        try {
            const res = await getData(`${API_ENDPOINTS.getTruckOwnerById}?owner_id=${ownerId}`);
            setOwner(res.result.owner_details);
            setCounts(res.result);
        } catch (_) { setError("Unable to fetch owner details."); }
        finally { setOwnerLoading(false); }
    }, [ownerId]);

    const fetchAgents = useCallback(async () => {
        if (!ownerId) return;
        setAgentsLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.adminGetAllAgents, { owner_id: ownerId, limit: 50 });
            setAgents(res.result?.agents || []);
        } catch (_) { setAgents([]); }
        finally { setAgentsLoading(false); }
    }, [ownerId]);

    const fetchFinancials = useCallback(async () => {
        if (!ownerId) return;
        setFinLoading(true);
        try {
            const [summaryRes, owedRes] = await Promise.allSettled([
                getData(API_ENDPOINTS.ownerInsightsSummary, { owner_id: ownerId }),
                getData(API_ENDPOINTS.ownerTripsOwed.replace(":owner_id", ownerId)),
            ]);
            if (summaryRes.status === "fulfilled") setFinSummary(summaryRes.value?.result ?? null);
            if (owedRes.status   === "fulfilled") setFinOwed(owedRes.value?.result ?? null);
        } catch (_) {}
        finally { setFinLoading(false); }
    }, [ownerId]);

    useEffect(() => { fetchOwner(); }, [fetchOwner]);
    useEffect(() => { if (tab === "team")      fetchAgents(); },    [tab, fetchAgents]);
    useEffect(() => { if (tab === "dashboard") fetchFinancials(); }, [tab, fetchFinancials]);

    if (ownerLoading) return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", p: 3 }}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: "20px", mb: 3 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
                {Array.from({length: 4}).map((_, i) => <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: "16px" }} />)}
            </Box>
        </Box>
    );

    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
    if (!owner) return <Typography p={3} color="text.secondary">No owner data found.</Typography>;

    const fullName    = `${owner.first_name || ""} ${owner.last_name || ""}`.trim();
    const [bg, fg]    = avatarBg(fullName);
    const totalTrucks = owner.Trucks?.length ?? counts?.trucks_count ?? 0;
    const onTrip      = owner.Trucks?.filter(t => t.tagged_to_trip).length ?? 0;
    const available   = totalTrucks - onTrip;
    const totalDrivers= counts?.drivers_count ?? 0;
    const isVerified  = owner.is_verified;

    const avgCapacity = owner.Trucks?.length
        ? Math.round(owner.Trucks.reduce((s, t) => s + (Number(t.capacity_inkgs) || 0), 0) / owner.Trucks.length)
        : 0;

    // ── Tabs content ──────────────────────────────────────────────────────

    // ── Dashboard chart data (derived from owner.Trucks) ─────────────────
    const pieData = [
        { name: "On Trip",   value: onTrip,   fill: T.teal   },
        { name: "Available", value: available, fill: T.orange },
    ];

    // Capacity tier buckets
    const capBuckets = [
        { label: "< 5t",   min: 0,     max: 5000  },
        { label: "5–10t",  min: 5000,  max: 10000 },
        { label: "10–20t", min: 10000, max: 20000 },
        { label: "> 20t",  min: 20000, max: Infinity },
    ];
    const capData = capBuckets.map(b => ({
        label: b.label,
        Trucks: (owner.Trucks || []).filter(t => {
            const kg = Number(t.capacity_inkgs) || 0;
            return kg >= b.min && kg < b.max;
        }).length,
    })).filter(d => d.Trucks > 0);

    // Per-truck capacity (top 12 by capacity, descending)
    const truckCapData = [...(owner.Trucks || [])]
        .sort((a, b) => (Number(b.capacity_inkgs) || 0) - (Number(a.capacity_inkgs) || 0))
        .slice(0, 12)
        .map(t => ({
            name: (t.truck_no || t.truck_name || "Truck").slice(0, 10),
            Capacity: Number(t.capacity_inkgs) || 0,
            fill: t.tagged_to_trip ? T.teal : T.orange,
        }));

    // Custom donut centre label
    const DonutLabel = ({ viewBox }) => {
        const { cx, cy } = viewBox || {};
        const pct = totalTrucks > 0 ? Math.round((onTrip / totalTrucks) * 100) : 0;
        return (
            <g>
                <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="central"
                    style={{ fontSize: 26, fontWeight: 800, fill: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {pct}%
                </text>
                <text x={cx} y={cy + 16} textAnchor="middle"
                    style={{ fontSize: 11, fill: T.muted, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    utilised
                </text>
            </g>
        );
    };

    // Chart tooltip
    const ChartTip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <Box sx={{ bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "10px", p: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
                {label && <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, mb: 0.5 }}>{label}</Typography>}
                {payload.map(p => (
                    <Box key={p.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.fill || p.color }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                            {p.name}: {p.name === "Capacity" ? `${p.value.toLocaleString("en-IN")} kg` : p.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    // ── Financial helpers ─────────────────────────────────────────────────
    const fmtRs = n => n == null ? "—" : Number(n) >= 100000
        ? `₹${(Number(n)/100000).toFixed(1)}L`
        : Number(n) >= 1000
            ? `₹${(Number(n)/1000).toFixed(1)}k`
            : `₹${Number(n).toFixed(0)}`;

    // ── Derive revenue from finOwed.trips (owner_subtotal_delivered per trip)
    // This works even when no payouts have been initiated yet — unlike the
    // insights endpoint which queries syt_initiated_owner_payments only.
    const allTrips = finOwed?.trips || [];
    const nowIst = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const todayIst  = new Date(nowIst.getTime() + istOffset);
    const weekStart = new Date(todayIst); weekStart.setUTCHours(0,0,0,0); weekStart.setUTCDate(weekStart.getUTCDate() - weekStart.getUTCDay() + 1);
    const monthStart= new Date(todayIst); monthStart.setUTCHours(0,0,0,0); monthStart.setUTCDate(1);
    const yearStart = new Date(todayIst); yearStart.setUTCHours(0,0,0,0); yearStart.setUTCMonth(0,1);

    const sumTrips = (trips, since) => trips.reduce((acc, t) => {
        const d = new Date(t.actual_delivery_at || t.trip_created_at || 0);
        if (d >= since) { acc.rev += Number(t.owner_subtotal_delivered || 0); acc.cnt += 1; }
        return acc;
    }, { rev: 0, cnt: 0 });

    const weekData  = sumTrips(allTrips, weekStart);
    const monthData = sumTrips(allTrips, monthStart);
    const ytdData   = sumTrips(allTrips, yearStart);

    const revenueBarData = (allTrips.length > 0) ? [
        { period: "This Week",  Revenue: weekData.rev,  Trips: weekData.cnt  },
        { period: "This Month", Revenue: monthData.rev, Trips: monthData.cnt },
        { period: "YTD",        Revenue: ytdData.rev,   Trips: ytdData.cnt   },
    ] : [];

    // Top unpaid trips (owed)
    const topOwedTrips = (finOwed?.trips || [])
        .filter(t => Number(t.net_owed_after_offset || t.owner_owed) > 0)
        .sort((a, b) => Number(b.net_owed_after_offset || b.owner_owed) - Number(a.net_owed_after_offset || a.owner_owed))
        .slice(0, 5);

    const DashboardTab = () => (
        <Box>
            {/* Fleet KPI row */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <KpiTile title="Total Trucks"  value={totalTrucks} icon={<LocalShippingRoundedIcon />} iconBg={T.tealLt}    iconColor={T.teal}   accent={T.teal}   sub={`${onTrip} on trip · ${available} available`} />
                <KpiTile title="On Trip"       value={onTrip}      icon={<DirectionsCarRoundedIcon />} iconBg={T.emeraldLt} iconColor={T.emerald}accent={T.emerald}sub="trucks currently active" />
                <KpiTile title="Available"     value={available}   icon={<LocalShippingRoundedIcon />} iconBg={T.orangeLt}  iconColor={T.orange} accent={T.orange} sub="ready for booking" />
                <KpiTile title="Total Drivers" value={totalDrivers}icon={<PeopleRoundedIcon />}       iconBg={T.amberLt}   iconColor={T.amber}  accent={T.amber}  sub="registered drivers" />
                <KpiTile title="Avg Capacity"  value={`${avgCapacity} kg`} icon={<SpeedRoundedIcon />} iconBg={T.indigoLt}  iconColor={T.indigo} accent={T.indigo} sub="per truck" />
            </Box>

            {/* ── Financial KPI row ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {finLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <Card key={i} sx={{ flex: "1 1 140px", borderRadius: "16px", border: `1px solid ${T.border}`, overflow: "hidden" }}>
                            <Box sx={{ height: 3, bgcolor: T.border }} />
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Skeleton width="60%" height={14} sx={{ mb: 1 }} />
                                <Skeleton width="50%" height={28} />
                            </CardContent>
                        </Card>
                    ))
                    : <>
                        {/* Revenue from owner_subtotal_delivered — works even before payouts initiated */}
                        <KpiTile title="This Month Revenue" value={monthData.rev > 0 ? fmtRs(monthData.rev) : "—"} icon={<SpeedRoundedIcon />}        iconBg={T.tealLt}    iconColor={T.teal}   accent={T.teal}   sub={monthData.cnt > 0 ? `${monthData.cnt} trips` : "no trips this month"} />
                        <KpiTile title="YTD Revenue"        value={ytdData.rev   > 0 ? fmtRs(ytdData.rev)   : "—"} icon={<SpeedRoundedIcon />}        iconBg={T.indigoLt}  iconColor={T.indigo} accent={T.indigo} sub={ytdData.cnt > 0 ? `${ytdData.cnt} trips this year` : "no trips this year"} />
                        <KpiTile title="Amount Owed"        value={fmtRs(finOwed?.total_owed_after_offset)}         icon={<PeopleRoundedIcon />}       iconBg={T.orangeLt}  iconColor={T.orange} accent={T.orange} sub="unpaid to owner" />
                        <KpiTile title="Wallet Balance"     value={fmtRs(finOwed?.wallet_balance)}                  icon={<DirectionsCarRoundedIcon />}iconBg={T.emeraldLt} iconColor={T.emerald}accent={T.emerald}sub="current balance" />
                        <KpiTile title="Avail. to Withdraw" value={fmtRs(finOwed?.available_to_withdraw)}           icon={<LocalShippingRoundedIcon />} iconBg={T.amberLt}   iconColor={T.amber}  accent={T.amber}  sub="net withdrawable" />
                    </>
                }
            </Box>

            {/* ── Revenue chart + Owed trips row ── */}
            {!finLoading && (revenueBarData.length > 0 || topOwedTrips.length > 0) && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>

                    {/* Revenue & trips by period */}
                    {revenueBarData.length > 0 && (
                        <Card sx={{ flex: "1 1 300px", borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, mb: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Revenue Overview</Typography>
                                <Typography sx={{ fontSize: 12, color: T.muted, mb: 1.5 }}>Earnings across periods</Typography>
                                <Box sx={{ height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={revenueBarData} barGap={4} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                            <defs>
                                                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%"   stopColor={T.teal}   stopOpacity={1} />
                                                    <stop offset="100%" stopColor={T.tealDk} stopOpacity={0.75} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                                            <XAxis dataKey="period" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} width={44}
                                                tickFormatter={v => v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`} />
                                            <RTooltip content={<ChartTip />} cursor={{ fill: T.tealLt }} />
                                            <Bar dataKey="Revenue" fill="url(#revGrad)" radius={[6, 6, 0, 0]} barSize={36} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>

                                {/* Week vs Month vs YTD summary pills */}
                                <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
                                    {revenueBarData.map(d => (
                                        <Box key={d.period} sx={{ flex: 1, minWidth: 80, bgcolor: T.bg, borderRadius: "10px", p: 1.2, textAlign: "center", border: `1px solid ${T.border}` }}>
                                            <Typography sx={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{d.period}</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: T.teal, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtRs(d.Revenue)}</Typography>
                                            <Typography sx={{ fontSize: 11, color: T.muted }}>{d.Trips} trip{d.Trips !== 1 ? "s" : ""}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Top unpaid trips */}
                    {topOwedTrips.length > 0 && (
                        <Card sx={{ flex: "1 1 300px", borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Pending Payouts</Typography>
                                        <Typography sx={{ fontSize: 12, color: T.muted }}>Top unpaid trips by amount</Typography>
                                    </Box>
                                    {finOwed?.total_owed_after_offset > 0 && (
                                        <Box sx={{ px: "10px", py: "4px", bgcolor: T.orangeLt, borderRadius: "8px" }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: T.orange }}>{fmtRs(finOwed.total_owed_after_offset)}</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {topOwedTrips.map((trip, i) => {
                                        const owed = Number(trip.net_owed_after_offset || trip.owner_owed || 0);
                                        const maxOwed = Number(topOwedTrips[0]?.net_owed_after_offset || topOwedTrips[0]?.owner_owed || 1);
                                        const pct = maxOwed > 0 ? (owed / maxOwed) * 100 : 0;
                                        return (
                                            <Box key={trip.trip_id}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.4 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Box sx={{ width: 20, height: 20, borderRadius: "6px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: T.teal }}>{i + 1}</Typography>
                                                        </Box>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                                                            Trip #{trip.trip_id}
                                                            {trip.from_city && trip.to_city && (
                                                                <Box component="span" sx={{ color: T.muted, fontWeight: 400 }}> · {trip.from_city} → {trip.to_city}</Box>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.orange }}>{fmtRs(owed)}</Typography>
                                                </Box>
                                                <Box sx={{ height: 4, borderRadius: "3px", bgcolor: T.border, overflow: "hidden" }}>
                                                    <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: T.orange, borderRadius: "3px", opacity: 0.7 }} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {finOwed?.pending_offset_total > 0 && (
                                    <Box sx={{ mt: 1.5, p: 1.2, bgcolor: T.amberLt, borderRadius: "10px", border: `1px solid ${T.amber}22` }}>
                                        <Typography sx={{ fontSize: 11, color: T.amber, fontWeight: 600 }}>
                                            ₹{Number(finOwed.pending_offset_total).toLocaleString("en-IN")} pending offset applied
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {/* Top route — from insights (uses syt_initiated_owner_payments, may be null) */}
            {!finLoading && finSummary?.topRoute && (
                <Box sx={{ mb: 3, p: 2, bgcolor: T.tealLt, borderRadius: "14px", border: `1px solid ${T.teal}22`, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ px: 2, py: 0.6, bgcolor: T.teal, borderRadius: "8px" }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.4px" }}>Top Route</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {finSummary.topRoute.startCity}
                        <Box component="span" sx={{ mx: 1, color: T.muted, fontWeight: 400 }}>→</Box>
                        {finSummary.topRoute.endCity}
                    </Typography>
                    {finSummary.topRoute.trips && (
                        <Typography sx={{ fontSize: 12, color: T.muted }}>· {finSummary.topRoute.trips} trips · {fmtRs(finSummary.topRoute.revenue)}</Typography>
                    )}
                </Box>
            )}

            {/* ── Charts row ── */}
            {totalTrucks > 0 && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>

                    {/* Donut — fleet status */}
                    <Card sx={{ flex: "1 1 240px", minWidth: 220, borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, mb: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Fleet Status</Typography>
                            <Typography sx={{ fontSize: 12, color: T.muted, mb: 1 }}>On Trip vs Available</Typography>
                            <Box sx={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%" cy="50%"
                                            innerRadius={55} outerRadius={80}
                                            paddingAngle={3}
                                            dataKey="value"
                                            labelLine={false}
                                        >
                                            {pieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.fill} stroke="none" />
                                            ))}
                                            <DonutLabel />
                                        </Pie>
                                        <RTooltip content={<ChartTip />} />
                                        <Legend
                                            iconType="circle" iconSize={8}
                                            wrapperStyle={{ fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Bar — capacity tier distribution */}
                    {capData.length > 0 && (
                        <Card sx={{ flex: "1 1 260px", borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, mb: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Capacity Distribution</Typography>
                                <Typography sx={{ fontSize: 12, color: T.muted, mb: 1 }}>Trucks per weight tier</Typography>
                                <Box sx={{ height: 200 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={capData} barSize={32} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                                            <defs>
                                                <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%"   stopColor={T.teal}   stopOpacity={1} />
                                                    <stop offset="100%" stopColor={T.tealDk} stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                            <RTooltip content={<ChartTip />} cursor={{ fill: T.tealLt }} />
                                            <Bar dataKey="Trucks" fill="url(#capGrad)" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Horizontal bar — per-truck capacity */}
                    {truckCapData.length > 0 && (
                        <Card sx={{ flex: "2 1 340px", borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, mb: 0.3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Truck Capacity</Typography>
                                <Typography sx={{ fontSize: 12, color: T.muted, mb: 1 }}>
                                    Top {truckCapData.length} trucks · <span style={{ color: T.teal, fontWeight: 600 }}>■</span> On Trip &nbsp;
                                    <span style={{ color: T.orange, fontWeight: 600 }}>■</span> Available
                                </Typography>
                                <Box sx={{ height: 200 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={truckCapData} layout="vertical" barSize={14} margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
                                            <CartesianGrid strokeDasharray="4 4" stroke={T.border} horizontal={false} />
                                            <XAxis type="number" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false}
                                                tickFormatter={v => `${(v/1000).toFixed(0)}t`} />
                                            <YAxis type="category" dataKey="name" width={68} tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} />
                                            <RTooltip content={<ChartTip />} cursor={{ fill: T.tealLt }} />
                                            <Bar dataKey="Capacity" radius={[0, 4, 4, 0]}>
                                                {truckCapData.map((entry, i) => (
                                                    <Cell key={i} fill={entry.fill} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {/* Trucks grid — click card to open Truck Details page */}
            <SectionHead title="Fleet Overview" count={totalTrucks} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
                {owner.Trucks?.map(t => (
                    <TruckCard key={t.truck_id} truck={t}
                        onView={id => navigate(`/app/truck-details/${encryptId(id)}`)} />
                ))}
            </Box>
        </Box>
    );

    const DetailsTab = () => (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {/* Profile */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                        <Avatar src={owner.pic} sx={{ width: 56, height: 56, bgcolor: bg, color: fg, fontSize: 20, fontWeight: 800 }}>
                            {!owner.pic && initials(fullName)}
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fullName}</Typography>
                            <Chip label={isVerified ? "Verified" : "Unverified"} size="small" sx={{
                                mt: 0.5, fontSize: 10, fontWeight: 700, height: 20, borderRadius: "6px",
                                bgcolor: isVerified ? T.emeraldLt : T.amberLt,
                                color:   isVerified ? T.emerald   : T.amber,
                            }} />
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <SectionHead title="Contact Information" />
                    <InfoRow icon={<PhoneOutlinedIcon />}       label="Mobile Number"  value={owner.mobile_num} />
                    <InfoRow icon={<EmailOutlinedIcon />}        label="Email Address"  value={owner.email || "N/A"} />
                    <InfoRow icon={<CalendarMonthRoundedIcon />} label="Member Since"   value={fmtDate(owner.createdAt)} />
                </CardContent>
            </Card>

            {/* Fleet stats card */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <SectionHead title="Fleet Summary" />
                    {[
                        { label: "Total Trucks",    value: totalTrucks,   color: T.teal,   bg: T.tealLt    },
                        { label: "On Active Trip",  value: onTrip,        color: T.emerald,bg: T.emeraldLt },
                        { label: "Available",       value: available,     color: T.orange, bg: T.orangeLt  },
                        { label: "Total Drivers",   value: totalDrivers,  color: T.amber,  bg: T.amberLt   },
                    ].map(s => (
                        <Box key={s.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                                <Typography sx={{ fontSize: 13, color: T.muted }}>{s.label}</Typography>
                            </Box>
                            <Box sx={{ px: "10px", py: "3px", bgcolor: s.bg, borderRadius: "8px", fontSize: 14, fontWeight: 800, color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {s.value}
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {/* Truck list removed — see Dashboard tab for the interactive fleet grid */}
        </Box>
    );

    const TeamTab = () => (
        <Box>
            {agentsLoading ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Card key={i} sx={{ borderRadius: "14px", border: `1px solid ${T.border}`, p: 2 }}>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <Skeleton variant="circular" width={44} height={44} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton width="70%" height={18} sx={{ mb: 0.8 }} />
                                    <Skeleton width="50%" height={14} />
                                </Box>
                            </Box>
                        </Card>
                    ))}
                </Box>
            ) : agents.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                    <SupportAgentRoundedIcon sx={{ fontSize: 48, color: T.border, mb: 1 }} />
                    <Typography sx={{ fontSize: 14, color: T.muted }}>No team members found for this owner.</Typography>
                </Box>
            ) : (
                <>
                    <SectionHead title="Team Members" count={agents.length} />
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
                        {agents.map(a => <AgentCard key={a.agent_id} agent={a} />)}
                    </Box>
                </>
            )}
        </Box>
    );

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <IconButton onClick={() => navigate("/app/truck-owners")}
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                        <ArrowBackIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Truck Owners</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Avatar src={owner.pic} sx={{ width: 60, height: 60, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 22, fontWeight: 800, border: "2px solid rgba(255,255,255,0.3)" }}>
                        {!owner.pic && initials(fullName)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2 }}>
                            {fullName}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.6, flexWrap: "wrap" }}>
                            <Chip label={isVerified ? "Verified" : "Unverified"} size="small" sx={{
                                height: 22, fontSize: 11, fontWeight: 700, borderRadius: "8px",
                                bgcolor: "rgba(255,255,255,0.2)", color: "#fff",
                                border: "1px solid rgba(255,255,255,0.3)",
                            }} />
                            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
                                {owner.mobile_num}
                            </Typography>
                            {owner.email && (
                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
                                    · {owner.email}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Tab bar ── */}
            <Box sx={{ display: "flex", gap: 0.5, bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "14px", p: 0.75, mb: 3, width: "fit-content" }}>
                {TABS.map(t => (
                    <TabBtn key={t.key} label={t.label} icon={t.icon} active={tab === t.key} onClick={() => setTab(t.key)} />
                ))}
            </Box>

            {/* ── Tab content ── */}
            {tab === "dashboard" && <DashboardTab />}
            {tab === "details"   && <DetailsTab />}
            {tab === "team"      && <TeamTab />}
        </Box>
    );
}
